import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  PhoneOutgoing,
  PhoneIncoming,
  FileText,
  Megaphone,
  Users,
  CalendarCheck,
  HeadphonesIcon,
  HelpCircle,
  ShoppingCart,
  Database,
  Calculator,
  FileCheck,
  ArrowRight,
  CheckCircle,
} from "lucide-react";

const Services = () => {
  const observerRef = useRef(null);

  const outboundServices = [
    {
      icon: Megaphone,
      title: "Telemarketing",
      description:
        "Strategic outbound campaigns to generate leads and boost sales.",
    },
    {
      icon: Users,
      title: "Lead Generation",
      description:
        "Identifying and qualifying potential customers for your business.",
    },
    {
      icon: CalendarCheck,
      title: "Appointment Setting",
      description:
        "Scheduling meetings with prospects to drive business opportunities.",
    },
  ];

  const inboundServices = [
    {
      icon: HeadphonesIcon,
      title: "Customer Support",
      description:
        "24/7 professional support ensuring customer satisfaction.",
    },
    {
      icon: HelpCircle,
      title: "Technical Helpdesk",
      description: "Expert technical assistance for product and service issues.",
    },
    {
      icon: ShoppingCart,
      title: "Order Processing",
      description:
        "Efficient handling of orders, returns, and customer inquiries.",
    },
  ];

  const backOfficeServices = [
    {
      icon: Database,
      title: "Data Entry",
      description:
        "Accurate and timely data entry and management services.",
    },
    {
      icon: Calculator,
      title: "Accounting Support",
      description:
        "Bookkeeping, invoicing, and financial documentation assistance.",
    },
    {
      icon: FileCheck,
      title: "Document Processing",
      description:
        "Digitization, verification, and management of business documents.",
    },
  ];

  const benefits = [
    "Dedicated account managers",
    "Real-time performance reporting",
    "Scalable team size",
    "Multi-channel support",
    "Quality assurance programs",
    "Data security compliance",
  ];

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".reveal").forEach((el) => {
      observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  const ServiceCard = ({ icon: Icon, title, description, index }) => (
    <div
      data-testid={`service-item-${title.toLowerCase().replace(/\s+/g, "-")}`}
      className="reveal bg-[#353537]/30 border border-white/5 hover:border-[#6DB432]/30 p-6 rounded-xl card-hover group pop-out spotlight-sweep float-card"
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      <div className="w-12 h-12 rounded-lg bg-[#6DB432]/20 flex items-center justify-center mb-4 group-hover:bg-[#6DB432]/30 transition-colors duration-300 icon-spin morph-shape">
        <Icon className="w-6 h-6 text-[#6DB432]" />
      </div>
      <h3 className="text-lg font-bold text-white mb-2 underline-grow">{title}</h3>
      <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
    </div>
  );

  return (
    <div data-testid="services-page" className="pt-20">
      {/* Hero Section */}
      <section 
        className="py-24 relative overflow-hidden"
        style={{
          backgroundImage: `url('/images/entrance.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#353537]/95 to-[#353537]/70" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl fade-in">
            <span className="text-[#6DB432] font-bold text-sm uppercase tracking-widest">
              Our Services
            </span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight">
              Comprehensive BPO
              <br />
              <span className="text-[#6DB432]">Solutions</span>
            </h1>
            <p className="mt-6 text-gray-300 text-lg sm:text-xl leading-relaxed">
              From customer support to back-office operations, we provide
              end-to-end outsourcing services tailored to your business needs.
            </p>
          </div>
        </div>
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-[#6DB432]/5 rounded-full blur-3xl -translate-y-1/2" />
      </section>

      {/* Outbound Services */}
      <section className="py-24 bg-[#0f0f10]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-12 reveal">
            <div className="w-14 h-14 rounded-lg bg-[#6DB432]/20 flex items-center justify-center">
              <PhoneOutgoing className="w-7 h-7 text-[#6DB432]" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white">
                Outbound Services
              </h2>
              <p className="text-gray-400">
                Proactive customer engagement solutions
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {outboundServices.map((service, index) => (
              <ServiceCard key={service.title} {...service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Inbound Services */}
      <section className="py-24 bg-[#353537]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-12 reveal">
            <div className="w-14 h-14 rounded-lg bg-[#6DB432]/20 flex items-center justify-center">
              <PhoneIncoming className="w-7 h-7 text-[#6DB432]" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white">
                Inbound Services
              </h2>
              <p className="text-gray-400">
                Responsive customer support solutions
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {inboundServices.map((service, index) => (
              <ServiceCard key={service.title} {...service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Back Office Services */}
      <section className="py-24 bg-[#0f0f10]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-12 reveal">
            <div className="w-14 h-14 rounded-lg bg-[#6DB432]/20 flex items-center justify-center">
              <FileText className="w-7 h-7 text-[#6DB432]" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white">
                Back Office Support
              </h2>
              <p className="text-gray-400">
                Administrative and operational excellence
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {backOfficeServices.map((service, index) => (
              <ServiceCard key={service.title} {...service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-[#353537]/30 relative overflow-hidden">
        {/* Decorative X mark */}
        <img
          src="/images/x-mark.png"
          alt=""
          className="absolute -left-10 top-1/2 -translate-y-1/2 h-[350px] w-auto opacity-[0.1] pointer-events-none object-contain"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="reveal">
              <span className="text-[#6DB432] font-bold text-sm uppercase tracking-widest">
                Why Choose Us
              </span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white leading-tight">
                Benefits of Working
                <br />
                <span className="text-[#6DB432]">With NUVEXA</span>
              </h2>
              <p className="mt-6 text-gray-400 leading-relaxed">
                We don't just provide services; we become an extension of your
                team. Our commitment to precision and quality ensures your
                business operations run smoothly.
              </p>
            </div>
            <div className="reveal">
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div
                    key={benefit}
                    data-testid={`benefit-${index}`}
                    className="flex items-center gap-3 bg-[#353537]/50 p-4 rounded-lg"
                  >
                    <CheckCircle className="w-5 h-5 text-[#6DB432] flex-shrink-0" />
                    <span className="text-white text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0f0f10]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center reveal">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Optimize Your Operations?
            </h2>
            <p className="text-[#6DB432] text-lg font-bold tracking-widest uppercase mb-8">
              Powered by PRECISION
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button
                  data-testid="services-contact-btn"
                  className="bg-[#6DB432] hover:bg-[#8BC064] text-white font-bold px-8 py-4 text-lg rounded-md btn-glow"
                >
                  Get a Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/about">
                <Button
                  data-testid="services-learn-more-btn"
                  variant="outline"
                  className="border-[#7B7B7D] text-white hover:border-[#6DB432] hover:text-[#6DB432] font-medium px-8 py-4 text-lg rounded-md bg-transparent"
                >
                  Learn More About Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
